import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormAlert } from '@/components/ui/form-alert';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { genericErrorMessage, httpStatus, serverMessage } from '@/lib/api-errors';
import { tenantsApi, type TenantDetail } from '@/lib/tenants-api';

// Mêmes bornes que suspendSchema (platform-api)
export const REASON_MIN = 3;
export const REASON_MAX = 500;

export const validateReason = (reason: string): string | null => {
  const value = reason.trim();
  if (value.length < REASON_MIN) return `Le motif est obligatoire (${REASON_MIN} caractères minimum)`;
  if (value.length > REASON_MAX) return `${REASON_MAX} caractères maximum`;
  return null;
};

/** 400 (statut incompatible) et 503 (Redis) portent un message utile ; le reste est générique. */
export const statusActionErrorMessage = (err: unknown): string => {
  const status = httpStatus(err);
  if (status === 404) return 'Tenant introuvable';
  if (status === 503) return 'Service momentanément indisponible : statut inchangé, réessayez';
  if (status === 400 && serverMessage(err)) return serverMessage(err)!;
  return genericErrorMessage(err);
};

interface DialogProps {
  tenant: Pick<TenantDetail, 'id' | 'name' | 'suspendedReason'>;
  open: boolean;
  onOpenChange(open: boolean): void;
}

const useStatusMutation = <V,>(fn: (v: V) => Promise<unknown>, success: string, onDone: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      toast.success(success);
      onDone();
      // Détail, liste et compteurs de l'accueil changent avec le statut
      await queryClient.invalidateQueries({ queryKey: ['platform'], predicate: (q) => q.queryKey[1] !== 'whoami' });
    },
  });
};

export const SuspendTenantDialog = ({ tenant, open, onOpenChange }: DialogProps) => {
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  // reset est appelé après coup (succès) : la fermeture vide aussi le motif saisi
  const mutation = useStatusMutation((r: string) => tenantsApi.suspend(tenant.id, r), `Tenant « ${tenant.name} » suspendu`, () =>
    reset(false),
  );

  const reset = (next: boolean) => {
    if (!next) {
      setReason('');
      setFieldError(null);
      mutation.reset();
    }
    onOpenChange(next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const invalid = validateReason(reason);
    setFieldError(invalid);
    if (!invalid) mutation.mutate(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <form onSubmit={submit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Suspendre le tenant</DialogTitle>
            <DialogDescription>
              Tous les utilisateurs de <span className="font-semibold text-foreground">{tenant.name}</span> perdent l'accès
              immédiatement (web, mobile, API). L'action est tracée et réversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="suspend-reason">Motif de la suspension</Label>
            <Textarea
              id="suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex. : impayés depuis 3 mois"
              maxLength={REASON_MAX}
              autoFocus
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError ? 'suspend-reason-error' : undefined}
            />
            <div className="flex justify-between text-xs">
              <span id="suspend-reason-error" className="text-danger">
                {fieldError}
              </span>
              <span className="text-muted-foreground tabular-nums">
                {reason.trim().length}/{REASON_MAX}
              </span>
            </div>
          </div>
          {mutation.isError && <FormAlert>{statusActionErrorMessage(mutation.error)}</FormAlert>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
              Annuler
            </Button>
            <Button type="submit" variant="destructive" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Confirmer la suspension
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ReactivateTenantDialog = ({ tenant, open, onOpenChange }: DialogProps) => {
  const mutation = useStatusMutation(() => tenantsApi.reactivate(tenant.id), `Tenant « ${tenant.name} » réactivé`, () =>
    reset(false),
  );

  const reset = (next: boolean) => {
    if (!next) mutation.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Réactiver le tenant</DialogTitle>
          <DialogDescription>
            Les utilisateurs de <span className="font-semibold text-foreground">{tenant.name}</span> retrouvent l'accès
            immédiatement. Le tenant repasse en essai si sa période d'essai n'est pas terminée, sinon en actif.
          </DialogDescription>
        </DialogHeader>
        {tenant.suspendedReason && (
          <p className="rounded-md bg-muted px-3 py-2 text-sm">
            <span className="text-muted-foreground">Motif de la suspension : </span>
            {tenant.suspendedReason}
          </p>
        )}
        {mutation.isError && <FormAlert>{statusActionErrorMessage(mutation.error)}</FormAlert>}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
            Annuler
          </Button>
          <Button
            type="button"
            className="bg-success text-white hover:bg-success/90"
            onClick={() => mutation.mutate(undefined)}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Confirmer la réactivation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
